import type { RecordTriageRequest } from '@wodi/shared';
import { formatEatTime } from '@wodi/domain';
import { Button, Notice } from '@wodi/ui';

import { retryQueue, type QueuedAction } from './retry-queue.ts';
import styles from './offline.module.css';

// Triage saved while offline (ADR 0018): kept on this device and sent when the connection
// is back, with the time the vitals were taken and the same clientRequestId, so a replay
// returns the stored record instead of recording it twice. The server checks it again
// when it arrives; if it refuses (e.g. an alert needs a reason), the entries are still in
// the form on this device and the nurse finishes it there.
// For review by the clinical safety lead: a queued triage is not in any clinician's queue
// until it is sent, so an urgent category tells the nurse to tell the clinician in person.

export async function queueTriage({
  visitId,
  patientId,
  request,
  href,
  draftKey,
  urgent,
}: {
  visitId: string;
  patientId: string;
  request: RecordTriageRequest;
  href: string;
  draftKey: string | null;
  urgent: boolean;
}): Promise<QueuedAction> {
  return retryQueue.enqueue({
    id: request.clientRequestId,
    kind: 'triage',
    target: visitId,
    label: 'Triage',
    href,
    method: 'POST',
    path: `/visits/${visitId}/triage`,
    // Clinical time is when it was taken, not when it reaches Wodi.
    body: { ...request, effectiveAt: request.effectiveAt ?? new Date().toISOString() },
    clearDraftKey: draftKey,
    refresh: [['visits', visitId, 'triage'], ['queues'], ['patients', patientId]],
    sentTitle: 'Triage saved',
    urgent,
  });
}

/** In place of the triage form while a saved triage waits on this device. */
export function QueuedTriageNotice({ item }: { item: QueuedAction }) {
  return (
    <div className={styles.queued} role="status">
      <Notice tone="info">
        {`Triage saved on this device at ${formatEatTime(new Date(item.savedAt))}. It is sent to Wodi when the connection is back. Then open the patient here to send them on.`}
      </Notice>
      {item.urgent ? (
        <Notice tone="error">
          This patient needs a clinician now. Tell the clinician in person: the triage is not in
          their queue until it is sent.
        </Notice>
      ) : null}
      <div>
        <Button
          variant="quiet"
          onPress={() => {
            void retryQueue.remove(item.id);
          }}
        >
          Change the triage
        </Button>
      </div>
    </div>
  );
}

/** Above the form when a triage saved on this device was refused by Wodi. */
export function RefusedTriageNotice({ item }: { item: QueuedAction }) {
  return (
    <Notice tone="error">
      {`The triage saved on this device at ${formatEatTime(new Date(item.savedAt))} wasn't recorded: ${item.error?.message ?? 'Wodi refused it.'} The entries are below. Check them and save again.`}
    </Notice>
  );
}
